import translations from "@/config/translations";
import useLangStore from "@/hooks/useLangStore";
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import "./styles.css";

export interface ActionsBarTab {
    label: string;
    path: string;
    end?: boolean;
}

export interface ActionsBarExtraAction {
    name: string;
    handler: () => void;
    children: ReactNode;
}

export interface ActionsBarProps {
    tabs?: ActionsBarTab[];
    onAdd?: () => void;
    addLabel?: string;
    extraActions?: ActionsBarExtraAction[];
    filterContent?: ReactNode;
    filterOpen?: boolean;
    onFilterOpen?: () => void;
    onFilterSave?: () => void;
    onFilterClear?: () => void;
    onFilterCancel?: () => void;
    hasActiveFilters?: boolean;
}

export default function ActionsBar({
    tabs,
    onAdd,
    addLabel,
    extraActions,
    filterContent,
    filterOpen,
    onFilterOpen,
    onFilterSave,
    onFilterClear,
    onFilterCancel,
    hasActiveFilters,
}: ActionsBarProps) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const hasFilter = filterContent !== undefined && filterContent !== null && onFilterOpen !== undefined;

    if (filterOpen) {
        return (
            <div className="actions-bar actions-bar--filter-mode">
                <div className="actions-bar-filter-scroll">{filterContent}</div>
                <div className="actions-bar-filter-ctas">
                    <button type="button" className="actions-bar-cta actions-bar-cta--clear" title={t.actionsBar.clearFilter} onClick={onFilterClear}>
                        <img src="/icons/clear-icon.svg" alt={t.actionsBar.clearFilter} width={18} height={18} />
                    </button>
                    <button
                        type="button"
                        className="actions-bar-cta actions-bar-cta--cancel"
                        title={t.actionsBar.cancelFilter}
                        onClick={onFilterCancel}
                    >
                        <img src="/icons/cancel-icon.svg" alt={t.actionsBar.cancelFilter} width={18} height={18} />
                    </button>
                    <button type="button" className="actions-bar-cta actions-bar-cta--save" title={t.actionsBar.saveFilter} onClick={onFilterSave}>
                        <img src="/icons/save-icon.svg" alt={t.actionsBar.saveFilter} width={18} height={18} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="actions-bar">
            {tabs && tabs.length > 0 && (
                <nav className="actions-bar-tabs">
                    {tabs.map((tab) => (
                        <NavLink
                            key={tab.path}
                            to={tab.path}
                            end={tab.end}
                            className={({ isActive }) => `actions-bar-tab${isActive ? " actions-bar-tab--active" : ""}`}
                        >
                            {tab.label}
                        </NavLink>
                    ))}
                </nav>
            )}
            <div className="actions-bar-spacer" />
            <div className="actions-bar-actions">
                {hasFilter && (
                    <button
                        type="button"
                        className={`actions-bar-filter-btn${hasActiveFilters ? " actions-bar-filter-btn--active" : ""}`}
                        onClick={onFilterOpen}
                        aria-label={t.actionsBar.filter}
                    >
                        <img src="/icons/filter-icon.svg" alt="" width={18} height={18} />
                        {hasActiveFilters && <span className="actions-bar-filter-dot" />}
                    </button>
                )}
                {extraActions?.map((action) => (
                    <button key={action.name} onClick={action.handler} type="button" className="actions-bar-extra-btn">
                        {action.children}
                    </button>
                ))}
                {onAdd && (
                    <button className="actions-bar-add-btn" onClick={onAdd} type="button">
                        <span>{addLabel ?? t.actionsBar.add}</span>
                        <img src="/icons/plus-icon.svg" alt="" width={12} height={12} />
                    </button>
                )}
            </div>
        </div>
    );
}
